import { z } from 'zod';
import { WhtChannelSchema } from './ap.js';
import { MoneySchema } from './common.js';
// NOTE: WhtChannelSchema is defined + exported by ap.ts and re-exported through
// the shared barrel from there; do NOT re-export it here too, or `import-x/export`
// flags a duplicate export on the barrel (index.ts re-exports both ap.js + wht.js).
// Decimal columns serialize either as a canonical string or as a coerced
// non-negative number — match the convention used in ar.ts / loan.ts.
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// Wire enums — mirror Prisma WhtFormType / WhtCertStatus / WhtFilingStatus.
// ============================================================
export const WhtFormTypeSchema = z.enum(['PND3', 'PND53', 'PND54']);
export const WhtCertStatusSchema = z.enum(['DRAFT', 'ISSUED', 'FILED', 'VOID']);
export const WhtFilingStatusSchema = z.enum(['DRAFT', 'FILED']);
export const WhtConditionSchema = z.enum(['WITHHELD', 'GROSS_UP', 'ONE_TIME']);
// ============================================================
// WhtCertificate
// ============================================================
// ============================================================
// WhtIncomeType master (B0 — de-hardcodes the 10 UI rates)
// ============================================================
// WhtIncomeTypeMasterSchema — the persisted DB record (de-hardcodes the 10 UI rates).
// Named "Master" to avoid collision with the ar.ts `WhtIncomeType` key-union type.
export const WhtIncomeTypeMasterSchema = z.object({
    code: z.string().min(1),
    labelTh: z.string().min(1),
    rate: DecimalLike,
    statute: z.string().nullable().optional(),
    defaultForm: WhtFormTypeSchema,
    eWhtEligible: z.boolean().default(false),
    eWhtRate: DecimalLike.nullable().optional(),
    eWhtRateFrom: z.coerce.date().nullable().optional(),
    eWhtRateTo: z.coerce.date().nullable().optional(),
});
// ============================================================
// WhtCertificate
// ============================================================
export const WhtCertificateSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    certDate: z.coerce.date(),
    branchId: z.uuid().nullable().optional(),
    vendorId: z.uuid().nullable().optional(),
    vendorName: z.string().min(1),
    vendorTaxId: z.string().min(1),
    vendorAddress: z.string().nullable().optional(),
    paymentRef: z.string().min(1),
    formType: WhtFormTypeSchema,
    incomeType: z.string().min(1),
    incomeDesc: z.string().min(1),
    base: DecimalLike,
    rate: DecimalLike,
    amount: DecimalLike,
    condition: z.string().default('WITHHELD'),
    description: z.string().nullable().optional(),
    status: WhtCertStatusSchema,
    postedBy: z.string().nullable().optional(),
    issuedAt: z.coerce.date().nullable().optional(),
    journalEntryId: z.uuid().nullable().optional(),
    // B-core B0: e-WHT channel + remittance fields (consumed by B-tax B4/B5).
    channel: WhtChannelSchema.default('NORMAL'),
    bankRemittanceRef: z.string().nullable().optional(),
    bankConfirmedAt: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Wire shape for POST — voucherNo / amount are server-derived. Rate may be
// supplied (UI presents the 10 standard categories), or the server can derive
// it from `incomeType`. We accept rate optionally so the API has the final say.
export const CreateWhtCertificateSchema = z.object({
    certDate: z.iso.date(),
    branchId: z.uuid().nullable().optional(),
    vendorId: z.uuid().nullable().optional(),
    vendorName: z.string().min(1),
    vendorTaxId: z.string().min(1),
    vendorAddress: z.string().optional(),
    paymentRef: z.string().min(1),
    formType: WhtFormTypeSchema,
    incomeType: z.string().min(1),
    incomeDesc: z.string().min(1),
    base: z.coerce.number().positive(),
    rate: z.coerce.number().min(0).max(100),
    condition: WhtConditionSchema.optional(),
    description: z.string().optional(),
    journalEntryId: z.uuid().nullable().optional(),
});
export const UpdateWhtCertificateSchema = z.object({
    status: WhtCertStatusSchema.optional(),
    notes: z.string().optional(),
});
// ============================================================
// WhtFiling — aggregate of certs by (period, formType)
// ============================================================
export const WhtFilingSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    period: z.string().regex(/^\d{6}$/),
    branchId: z.uuid().nullable().optional(),
    formType: WhtFormTypeSchema,
    filingDate: z.coerce.date(),
    totalBase: DecimalLike,
    totalWht: DecimalLike,
    count: z.number().int().nonnegative(),
    status: WhtFilingStatusSchema,
    fileRef: z.string().nullable().optional(),
    filedBy: z.string().nullable().optional(),
    filedAt: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const CreateWhtFilingSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
    // null/omitted = head-office consolidated; a branchId files that tax-branch.
    branchId: z.uuid().nullable().optional(),
    formType: WhtFormTypeSchema,
    filingDate: z.iso.date(),
    fileRef: z.string().optional(),
});
export const UpdateWhtFilingSchema = z.object({
    status: WhtFilingStatusSchema.optional(),
    fileRef: z.string().nullable().optional(),
});
// ============================================================
// B5 — confirm-remittance (e-WHT bank reconciliation)
// PATCH .../wht/certs/:id/confirm-remittance — books the WHT_PAYABLE
// clearing JV (Dr WHT_PAYABLE / Cr bank) and stamps the bank's remittance
// proof on the e-WHT cert. Only applicable to channel=E_WHT certs.
// ============================================================
export const ConfirmWhtRemittanceSchema = z.object({
    bankRemittanceRef: z.string().min(1),
    bankConfirmedAt: z.iso.date(),
    cashAccountCode: z.string().min(1), // bank account credited for the remittance
});
// ============================================================
// Summary — for filing prep (aggregated by income type)
// ============================================================
export const WhtSummaryRowSchema = z.object({
    incomeType: z.string(),
    incomeDesc: z.string(),
    count: z.number().int().nonnegative(),
    base: DecimalLike,
    wht: DecimalLike,
});
export const WhtSummarySchema = z.object({
    period: z.string().regex(/^\d{6}$/),
    formType: WhtFormTypeSchema,
    totalBase: DecimalLike,
    totalWht: DecimalLike,
    count: z.number().int().nonnegative(),
    byIncomeType: z.array(WhtSummaryRowSchema),
});
//# sourceMappingURL=wht.js.map